'use client';

import { useState, FormEvent } from 'react';
import { createClient } from '@/lib/supabase/client';

interface NewsletterSignupProps {
  source?: string;
  className?: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export default function NewsletterSignup({ source = 'website', className = '' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const supabase = createClient();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .insert({ email, source })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          // Handle duplicate email or other data errors gracefully
          if (error.code === '23505') {
            setMessage({ type: 'error', text: 'This email is already subscribed!' });
          } else {
            setMessage({ type: 'error', text: 'Failed to subscribe. Please try again.' });
          }
          setLoading(false);
          return;
        }
      }

      setMessage({ type: 'success', text: 'Successfully subscribed! Check your inbox for exclusive contractor tips.' });
      setEmail('');
    } catch (error: any) {
      console.error('Schema-related error:', error.message);
      setMessage({ type: 'error', text: 'System error. Please contact support.' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 shadow-lg ${className}`}>
      <div className="max-w-2xl mx-auto text-center">
        <h3 className="text-2xl font-bold text-white mb-2">
          Join 5,000+ Contractors Getting Pricing Insights
        </h3>
        <p className="text-blue-100 mb-6">
          Get exclusive market trends, pricing strategies, and HVAC business tips delivered weekly. Plus, receive our free "Competitive Pricing Guide" instantly!
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:border-white focus:outline-none text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? 'Subscribing...' : 'Get Free Guide'}
          </button>
        </form>

        {message && (
          <div className={`mt-4 p-3 rounded-lg ${
            message.type === 'success' ?'bg-green-500 text-white' :'bg-red-500 text-white'
          }`}>
            {message.text}
          </div>
        )}

        <p className="text-blue-100 text-sm mt-4">
          No spam. Unsubscribe anytime. Your email is safe with us.
        </p>
      </div>
    </div>
  );
}